import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MapPin,
  CheckCircle2,
  Building2,
  Star,
  Lock,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import ContactForm from "../../../components/ContactForm/ContactForm";
import PropertyCard from "../../../components/PropertyCard/PropertyCard";
import { getPropertyById, getProperties } from "../../../services/api";
import "./PropertyDetails.css";

const UNLOCK_KEY = "re_details_unlocked";

function readUnlocked() {
  try {
    return window.localStorage.getItem(UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
}

function formatPrice(value) {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(String(value).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return String(value);
  return `₹${n.toLocaleString("en-IN")}`;
}

/** Map API property → fields the page uses */
function normalizeProperty(p) {
  if (!p) return null;

  const images = Array.isArray(p.images)
    ? p.images.filter(Boolean)
    : p.cover || p.image
    ? [p.cover || p.image]
    : [];

  const units = Array.isArray(p.units) ? p.units : [];
  const unitPrices = units
    .map((u) => Number(u.price))
    .filter((n) => Number.isFinite(n) && n > 0);
  const startingPrice =
    unitPrices.length > 0
      ? Math.min(...unitPrices)
      : p.Price ?? p.price ?? null;

  return {
    ...p,
    id: p.id,
    name: p.title || p.name || "—",
    builder: p.developer || p.builder || "—",
    location:
      [p.locality, p.city].filter(Boolean).join(", ") ||
      p.address ||
      p.location ||
      "—",
    category: p.category || "—",
    possession: p.possessionStatus || p.possession || "—",
    rera: p.rera || "—",
    priceLabel: formatPrice(startingPrice),
    configuration: units.map((u) => u.unitType).filter(Boolean),
    gallery: images,
    cover: images[0] || "",
    description: p.description || "",
    highlights: p.keyFeatures || p.highlights || [],
    amenities: p.amenities || [],
    locationAdvantages: p.locationAdvantages || [],
    landmarks: p.landmarks || p.nearby || [],
    lifestyleScores: p.lifestyleScores || [],
    disclaimer: p.disclaimer || "",
    // floor plans from units that have a floorPlan image
    floorPlans: units
      .filter((u) => u.unitType)
      .map((u) => ({
        label: u.unitType,
        size: u.area ? `${u.area} sq.ft` : "—",
        price: formatPrice(u.price),
        image: u.floorPlan || null,
      })),
  };
}

export default function PropertyDetails() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setUnlocked(readUnlocked());
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setActiveImg(0);
    setProperty(null);
    setRelated([]);

    (async () => {
      try {
        const raw = await getPropertyById(id);
        if (cancelled) return;

        const p = normalizeProperty(raw);
        setProperty(p);

        if (p) {
          try {
            const all = await getProperties();
            const rel = (all || [])
              .filter(
                (x) =>
                  String(x.id) !== String(p.id) &&
                  (String(x.category || "").toLowerCase() ===
                    String(p.category || "").toLowerCase() ||
                    String(x.city || "").toLowerCase() ===
                      String(p.city || "").toLowerCase())
              )
              .slice(0, 3)
              .map(normalizeProperty)
              .filter(Boolean);
            if (!cancelled) setRelated(rel);
          } catch {
            if (!cancelled) setRelated([]);
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setProperty(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (unlocked || !property || loading) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setGateOpen(true);
      },
      { rootMargin: "0px 0px -20% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [unlocked, property, loading]);

  useEffect(() => {
    document.body.style.overflow = gateOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [gateOpen]);

  function handleUnlock() {
    try {
      window.localStorage.setItem(UNLOCK_KEY, "1");
    } catch {
      /* ignore */
    }
    setUnlocked(true);
    setGateOpen(false);
  }

  if (loading) {
    return (
      <div className="pd-page">
        <Navbar />
        <div className="pd-empty">Loading project…</div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pd-page">
        <Navbar />
        <div className="pd-empty">
          <h1>Project not found</h1>
          <Link to="/" className="pd-back-link">
            Back home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const gallery =
    property.gallery?.length > 0
      ? property.gallery
      : property.cover
      ? [property.cover]
      : [];

  return (
    <div className="pd-page">
      <Navbar />

      {/* Gallery */}
      <section className="pd-gallery-section">
        <div className="pd-container">
          <div className="pd-gallery">
            <div className="pd-main-image">
              {gallery[activeImg] ? (
                <img
                  src={gallery[activeImg]}
                  alt={property.name}
                />
              ) : (
                <div className="pd-main-image-empty">No image</div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="pd-thumbs">
                {gallery.slice(0, 6).map((g, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImg(i)}
                    className={`pd-thumb ${
                      activeImg === i ? "is-active" : ""
                    }`}
                  >
                    <img src={g} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content + Enquiry */}
      <section className="pd-content-section">
        <div className="pd-container pd-layout">
          <div className="pd-main">
            <div className="pd-builder">
              <Building2 className="h-3.5 w-3.5" />
              {property.builder}
            </div>

            <h1 className="pd-title">{property.name}</h1>

            <div className="pd-meta">
              <span className="pd-meta-item">
                <MapPin className="h-4 w-4" />
                {property.location}
              </span>
              <span>·</span>
              <span className="capitalize">{property.category}</span>
              <span>·</span>
              <span>{property.possession}</span>
            </div>

            <div className="pd-stats">
              <Stat k="Starting" v={property.priceLabel} />
              <Stat
                k="Config"
                v={
                  property.configuration?.length
                    ? property.configuration.join(", ")
                    : "—"
                }
              />
              <Stat k="RERA" v={property.rera || "—"} />
              <Stat k="Possession" v={property.possession || "—"} />
            </div>

            <Block title="Overview">
              <p className="pd-text">{property.description || "—"}</p>
            </Block>

            <div ref={sentinelRef} className="pd-sentinel" />

            {!unlocked ? (
              <div className="pd-locked">
                <div className="pd-locked-fade" aria-hidden="true">
                  <div className="pd-skeleton-title" />
                  <div className="pd-skeleton-grid">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="pd-skeleton-row" />
                    ))}
                  </div>
                </div>
                <div className="pd-locked-cta">
                  <Lock className="h-5 w-5 text-gold" />
                  <div>
                    <div className="pd-locked-title">
                      Full project details are locked
                    </div>
                    <p className="pd-locked-sub">
                      Share your contact details to unlock highlights,
                      amenities, floor plans, location and more.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGateOpen(true)}
                    className="pd-unlock-btn"
                  >
                    Unlock details
                  </button>
                </div>
              </div>
            ) : (
              <>
                {property.highlights?.length > 0 && (
                  <Block title="Project Highlights">
                    <ul className="pd-highlights">
                      {property.highlights.map((h) => (
                        <li key={h}>
                          <Star className="h-4 w-4 text-gold" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </Block>
                )}

                {property.locationAdvantages?.length > 0 && (
                  <Block title="Location Advantages">
                    <ul className="pd-highlights">
                      {property.locationAdvantages.map((h) => (
                        <li key={h}>
                          <CheckCircle2 className="h-4 w-4 text-gold" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </Block>
                )}

                {property.amenities?.length > 0 && (
                  <Block title="Amenities">
                    <div className="pd-amenities">
                      {property.amenities.map((a) => (
                        <span key={a} className="pd-amenity">
                          {a}
                        </span>
                      ))}
                    </div>
                  </Block>
                )}

                {property.floorPlans?.length > 0 && (
                  <Block title="Floor Plans & Units">
                    <div className="pd-floorplans">
                      {property.floorPlans.map((fp, i) => (
                        <div key={`${fp.label}-${i}`} className="pd-fp-card">
                          {fp.image && (
                            <div className="pd-fp-image">
                              <img
                                src={fp.image}
                                alt={fp.label}
                                loading="lazy"
                              />
                            </div>
                          )}
                          <div className="pd-fp-label">{fp.label}</div>
                          <div className="pd-fp-size">{fp.size}</div>
                          <div className="pd-fp-price">{fp.price}</div>
                        </div>
                      ))}
                    </div>
                  </Block>
                )}

                {property.landmarks?.length > 0 && (
                  <Block title="Location & Nearby">
                    <ul className="pd-nearby">
                      {property.landmarks.map((n) => (
                        <li key={n.name}>
                          <span className="pd-nearby-name">
                            <MapPin className="h-4 w-4 text-gold" />
                            {n.name}
                          </span>
                          <span className="pd-nearby-dist">
                            {n.distance}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Block>
                )}

                {property.lifestyleScores?.length > 0 && (
                  <Block title="Lifestyle Scores">
                    <ul className="pd-nearby">
                      {property.lifestyleScores.map((s) => (
                        <li key={s.category}>
                          <span className="pd-nearby-name">{s.category}</span>
                          <span className="pd-nearby-dist">{s.score}/100</span>
                        </li>
                      ))}
                    </ul>
                  </Block>
                )}

                <Block title="Builder">
                  <div className="pd-builder-card">
                    <div className="pd-builder-name">{property.builder}</div>
                    <p>
                      Trusted developer with a strong delivery track record
                      and RERA-verified projects.
                    </p>
                  </div>
                </Block>

                {property.rera && property.rera !== "—" && (
                  <Block title="RERA">
                    <div className="pd-rera">
                      <CheckCircle2 className="h-4 w-4 text-gold" />
                      RERA No.: <strong>{property.rera}</strong>
                    </div>
                  </Block>
                )}

                {property.disclaimer && (
                  <p className="pd-text" style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                    {property.disclaimer}
                  </p>
                )}
              </>
            )}
          </div>

          <aside className="pd-aside">
            <div className="pd-enquiry-card">
              <div className="pd-enquiry-label">Enquire about</div>
              <div className="pd-enquiry-name">{property.name}</div>
              <div className="pd-enquiry-price">{property.priceLabel}</div>
              <div className="pd-enquiry-form">
                <ContactForm compact propertyName={property.name} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="pd-related-section">
          <div className="pd-container">
            <h2 className="pd-related-title">Related projects</h2>
            <div className="pd-related-grid">
              {related.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {gateOpen && !unlocked && (
        <div
          className="pd-gate"
          role="dialog"
          aria-modal="true"
          aria-label="Enquiry required"
        >
          <div className="pd-gate-card">
            <div className="pd-gate-badge">
              <Lock className="h-3.5 w-3.5" /> Enquiry required
            </div>
            <h3 className="pd-gate-title">
              Unlock full details of {property.name}
            </h3>
            <p className="pd-gate-sub">
              Fill in your details and our advisor will share pricing, floor
              plans and availability. Submitting unlocks the complete project
              page instantly.
            </p>
            <div className="pd-gate-form">
              <ContactForm
                compact
                propertyName={property.name}
                onSuccess={handleUnlock}
                submitLabel="Submit & unlock details"
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="pd-stat">
      <div className="pd-stat-k">{k}</div>
      <div className="pd-stat-v">{v}</div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="pd-block">
      <h2 className="pd-block-title">{title}</h2>
      <div className="pd-block-body">{children}</div>
    </div>
  );
}